/**
 * OperationalBundleViewer
 *
 * Main orchestrator for the independent Operational Bundle Viewer.
 *
 * Flow:
 * 1. idle → dropzone visible
 * 2. loading → parse JSON
 * 3. verifying → run independent verification
 * 4. valid → render replay workspace with verification bar
 * 5. invalid → show failure state with verification results
 * 6. error → show parse/runtime error
 *
 * INVARIANTS:
 * - No backend dependency
 * - No execution authority
 * - Fail closed on any invalid bundle
 * - proofOnly-safe by construction
 */

import React, { useReducer, useCallback, useState } from 'react';
import { verifyOperationalBundle } from '../server/export/verifyOperationalBundle.js';
import { verifyOperationalBundleSignature } from '../governanceWorkspace/attestation/verifyOperationalBundleSignature.js';
import { deserializeOperationalWorkspace } from '../server/export/deserializeOperationalWorkspace.js';
import { buildOperationalLineageGraph } from '../governanceWorkspace/lineage/buildOperationalLineageGraph.js';
import { verifyOperationalLineageGraph } from '../governanceWorkspace/lineage/verifyOperationalLineageGraph.js';
import { NEUTRAL } from '../modules/governanceEventStream/operationalVisualLanguage/index.js';
import { viewerReducer, INITIAL_VIEWER_STATE } from './operationalBundleViewerState.js';
import { OperationalBundleDropzone } from './OperationalBundleDropzone.js';
import { OperationalBundleLoader } from './OperationalBundleLoader.js';
import { OperationalBundleVerifier } from './OperationalBundleVerifier.js';
import { OperationalBundleFailureState } from './OperationalBundleFailureState.js';
import { OperationalBundleReplayWorkspace } from './OperationalBundleReplayWorkspace.js';
import { OperationalLineageGraphPanel } from '../governanceWorkspace/lineage/ui/OperationalLineageGraphPanel.js';
import type { OperationalBundleV1 } from '../server/export/operationalBundleTypes.js';

export const OperationalBundleViewer: React.FC = () => {
  const [state, dispatch] = useReducer(viewerReducer, INITIAL_VIEWER_STATE);
  const [activeTab, setActiveTab] = useState<'replay' | 'lineage'>('replay');

  const handleBundleLoaded = useCallback((bundleJson: unknown) => {
    dispatch({ type: 'LOAD_START' });

    try {
      const bundle = bundleJson as OperationalBundleV1;
      dispatch({ type: 'LOAD_SUCCESS', bundle });

      // Trigger async verification (bundle integrity + attestation + lineage)
      Promise.all([
        verifyOperationalBundle(bundle),
        verifyOperationalBundleSignature(bundle),
        buildOperationalLineageGraph(bundle),
      ]).then(async ([result, attestation, lineageGraph]) => {
        const lineageVerification = await verifyOperationalLineageGraph(lineageGraph, bundle);
        if (result.valid) {
          try {
            const workspace = deserializeOperationalWorkspace(bundle);
            dispatch({ type: 'VERIFY_SUCCESS', result, workspace, attestation, lineageGraph, lineageVerification });
          } catch (err) {
            dispatch({
              type: 'VERIFY_FAILURE',
              result: {
                ...result,
                valid: false,
                errors: [...result.errors, `Deserialization failed: ${err instanceof Error ? err.message : String(err)}`],
              },
              workspace: null,
              attestation,
              lineageGraph,
              lineageVerification,
            });
          }
        } else {
          let workspace: ReturnType<typeof deserializeOperationalWorkspace> | null = null;
          try {
            // Attempt deserialization even for invalid bundles to allow metadata inspection
            workspace = deserializeOperationalWorkspace(bundle);
          } catch {
            // Deserialization failed — workspace remains null
          }
          dispatch({ type: 'VERIFY_FAILURE', result, workspace, attestation, lineageGraph, lineageVerification });
        }
      });
    } catch (err) {
      dispatch({
        type: 'LOAD_ERROR',
        message: err instanceof Error ? err.message : 'Failed to process bundle',
      });
    }
  }, []);

  const handleError = useCallback((message: string) => {
    dispatch({ type: 'LOAD_ERROR', message });
  }, []);

  const handleReset = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, []);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      backgroundColor: NEUTRAL.bg100,
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        padding: '8px 16px',
        backgroundColor: NEUTRAL.bg200,
        borderBottom: `1px solid ${NEUTRAL.border}`,
        gap: 12,
      }}>
        <span style={{
          fontSize: 14,
          fontWeight: 600,
          color: NEUTRAL.text600,
        }}>
          Operational Bundle Viewer
        </span>
        <span style={{
          fontSize: 11,
          color: NEUTRAL.text500,
        }}>
          Independent verification · Offline replay · proofOnly-safe
        </span>
        {state.phase !== 'idle' && (
          <button
            onClick={handleReset}
            style={{
              marginLeft: 'auto',
              padding: '4px 12px',
              fontSize: 11,
              fontWeight: 600,
              color: NEUTRAL.text500,
              backgroundColor: NEUTRAL.bg300,
              border: `1px solid ${NEUTRAL.border}`,
              borderRadius: 3,
              cursor: 'pointer',
            }}
            type="button"
          >
            Reset
          </button>
        )}
      </div>

      {/* Verification bar (shown for valid/invalid states) */}
      {state.bundle && state.verification && (
        <OperationalBundleVerifier
          bundle={state.bundle}
          verification={state.verification}
          attestationResult={state.attestation}
        />
      )}

      {/* Main content area */}
      <div style={{ flex: 1, overflow: 'hidden' }}>
        {state.phase === 'idle' && (
          <div style={{ padding: 48, maxWidth: 640, margin: '0 auto' }}>
            <OperationalBundleDropzone
              onBundleLoaded={handleBundleLoaded}
              onError={handleError}
            />
          </div>
        )}

        {(state.phase === 'loading' || state.phase === 'verifying') && (
          <OperationalBundleLoader
            message={state.phase === 'loading' ? 'Parsing bundle…' : 'Verifying bundle integrity…'}
          />
        )}

        {state.phase === 'valid' && state.workspace && state.bundle && (
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div style={{
              display: 'flex',
              gap: 0,
              borderBottom: `1px solid ${NEUTRAL.border}`,
              backgroundColor: NEUTRAL.bg200,
            }}>
              <button
                onClick={() => setActiveTab('replay')}
                style={{
                  padding: '6px 16px',
                  fontSize: 12,
                  fontWeight: 600,
                  border: 'none',
                  borderBottom: activeTab === 'replay' ? `2px solid ${NEUTRAL.text600}` : '2px solid transparent',
                  backgroundColor: activeTab === 'replay' ? NEUTRAL.bg100 : 'transparent',
                  color: activeTab === 'replay' ? NEUTRAL.text600 : NEUTRAL.text500,
                  cursor: 'pointer',
                }}
                type="button"
                data-testid="tab-replay"
              >
                Replay
              </button>
              <button
                onClick={() => setActiveTab('lineage')}
                style={{
                  padding: '6px 16px',
                  fontSize: 12,
                  fontWeight: 600,
                  border: 'none',
                  borderBottom: activeTab === 'lineage' ? `2px solid ${NEUTRAL.text600}` : '2px solid transparent',
                  backgroundColor: activeTab === 'lineage' ? NEUTRAL.bg100 : 'transparent',
                  color: activeTab === 'lineage' ? NEUTRAL.text600 : NEUTRAL.text500,
                  cursor: 'pointer',
                }}
                type="button"
                data-testid="tab-lineage"
              >
                Lineage
              </button>
            </div>
            <div style={{ flex: 1, overflow: 'hidden' }}>
              {activeTab === 'replay' && (
                <OperationalBundleReplayWorkspace
                  workspace={state.workspace}
                  isFixture={state.bundle.fixtureIndicator || false}
                />
              )}
              {activeTab === 'lineage' && (
                <OperationalLineageGraphPanel
                  graph={state.lineageGraph}
                  verification={state.lineageVerification}
                />
              )}
            </div>
          </div>
        )}

        {state.phase === 'invalid' && state.verification && (
          <OperationalBundleFailureState
            reason="malformed"
            errorMessage={state.errorMessage}
            verification={state.verification}
            onReset={handleReset}
          />
        )}

        {state.phase === 'error' && (
          <OperationalBundleFailureState
            reason="parse-error"
            errorMessage={state.errorMessage}
            onReset={handleReset}
          />
        )}
      </div>
    </div>
  );
};
